import {
  ATTRIBUTION_STORAGE_KEY,
  type AttributionContext,
  CURRENT_ATTRIBUTION_VERSION,
  isAttributionContext,
} from "@nextday-ai/common-ts-model";

/**
 * Reads the persisted envelope. Returns `undefined` on missing, unparseable,
 * shape-invalid, or version-mismatched values. All localStorage access is
 * guarded so SSR callers get `undefined` without throwing.
 */
export const readAttributionContext = (): AttributionContext | undefined => {
  if (typeof window === "undefined") {
    return undefined;
  }

  let raw: string | undefined;
  try {
    raw = window.localStorage.getItem(ATTRIBUTION_STORAGE_KEY) ?? undefined;
  } catch {
    return undefined;
  }
  if (raw === undefined || raw === "") return undefined;

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return undefined;
  }
  if (!isAttributionContext(parsed) || parsed.version !== CURRENT_ATTRIBUTION_VERSION) {
    return undefined;
  }
  return parsed;
};

/** Persists the envelope; swallows localStorage errors. Attribution is telemetry, never a blocker. */
export const writeAttributionContext = (context: AttributionContext): void => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(context));
  } catch {
    // Swallow.
  }
};

/** Removes the persisted envelope. */
export const clearAttributionContext = (): void => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(ATTRIBUTION_STORAGE_KEY);
  } catch {
    // Swallow.
  }
};
